/**
 * TrendMart Core JavaScript Framework
 * Modern Supermarket OS 2.0 Event Bus & Utilities
 */

window.TrendMart = window.TrendMart || {};

// Pub/Sub Event Bus
TrendMart.pubsub = {
  events: {},
  subscribe(event, callback) {
    if (!this.events[event]) this.events[event] = [];
    this.events[event].push(callback);
    return () => this.unsubscribe(event, callback);
  },
  unsubscribe(event, callback) {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(cb => cb !== callback);
  },
  publish(event, data) {
    if (!this.events[event]) return;
    this.events[event].forEach(cb => {
      try {
        cb(data);
      } catch (err) {
        console.error(`Error in event listener for ${event}:`, err);
      }
    });
  }
};

// Utilities
TrendMart.utils = {
  debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },

  formatMoney(cents, format = '$ {{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '$ {{amount}}';

    function defaultOption(opt, def) {
      return typeof opt === 'undefined' ? def : opt;
    }

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = defaultOption(precision, 2);
      thousands = defaultOption(thousands, ',');
      decimal = defaultOption(decimal, '.');

      if (isNaN(number) || number == null) return 0;
      number = (number / 100.0).toFixed(precision);
      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
    }

    return formatString.replace(placeholderRegex, value);
  },

  trapFocus(element) {
    const focusableEls = element.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input[type="text"]:not([disabled]), input[type="radio"]:not([disabled]), input[type="checkbox"]:not([disabled]), select:not([disabled])');
    const firstFocusableEl = focusableEls[0];
    const lastFocusableEl = focusableEls[focusableEls.length - 1];

    element.addEventListener('keydown', function(e) {
      const isTabPressed = (e.key === 'Tab' || e.keyCode === 9);
      if (!isTabPressed) return;

      if (e.shiftKey) {
        if (document.activeElement === firstFocusableEl) {
          lastFocusableEl.focus();
          e.preventDefault();
        }
      } else {
        if (document.activeElement === lastFocusableEl) {
          firstFocusableEl.focus();
          e.preventDefault();
        }
      }
    });

    if (firstFocusableEl) firstFocusableEl.focus();
  }
};

// Quantity Selector Handler
document.addEventListener('DOMContentLoaded', () => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-quantity-decrease], [data-quantity-increase]');
    if (!btn) return;

    const wrapper = btn.closest('[data-quantity-wrapper]');
    if (!wrapper) return;

    const input = wrapper.querySelector('[data-quantity-input]');
    if (!input) return;

    let val = parseInt(input.value, 10) || 1;
    const min = parseInt(input.min, 10) || 1;
    const max = input.max ? parseInt(input.max, 10) : null;

    if (btn.hasAttribute('data-quantity-decrease')) {
      if (val > min) {
        input.value = val - 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else if (btn.hasAttribute('data-quantity-increase')) {
      if (!max || val < max) {
        input.value = val + 1;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });

  // Product Card Color Swatches Delegated Listener
  document.addEventListener('click', (e) => {
    const dot = e.target.closest('[data-swatch-dot], .tm-swatch-dot');
    if (!dot) return;

    // Do not navigate or submit form
    e.preventDefault();
    e.stopPropagation();

    const card = dot.closest('[data-product-card], .tm-product-card');
    if (!card) return;

    // 1. Add .is-active on the clicked dot, remove from siblings
    const swatchesWrap = dot.closest('.tm-card-swatches');
    if (swatchesWrap) {
      swatchesWrap.querySelectorAll('.tm-swatch-dot').forEach(b => b.classList.remove('is-active'));
    }
    dot.classList.add('is-active');

    // 2. Swap the card image to that variant image
    const newImg = dot.getAttribute('data-image');
    if (newImg) {
      const primaryImg = card.querySelector('.primary-img, .tm-product-card__img-link img');
      if (primaryImg) {
        primaryImg.src = newImg;
        if (primaryImg.hasAttribute('srcset')) {
          primaryImg.removeAttribute('srcset');
        }
      }
      const secondaryImg = card.querySelector('.secondary-img');
      if (secondaryImg) {
        secondaryImg.style.display = 'none';
      }
    }

    // 3. Set the card form hidden name="id" to that variant
    const variantId = dot.getAttribute('data-variant-id');
    if (variantId) {
      const idInput = card.querySelector('input[name="id"]');
      if (idInput) {
        idInput.value = variantId;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // 4. Update the card price if changed
    const priceRaw = dot.getAttribute('data-price');
    const compareRaw = dot.getAttribute('data-compare');
    if (priceRaw && window.TrendMart && TrendMart.utils && TrendMart.utils.formatMoney) {
      const price = parseInt(priceRaw, 10);
      const compare = parseInt(compareRaw, 10) || 0;
      const priceBox = card.querySelector('.tm-price-box');
      if (priceBox && !isNaN(price)) {
        const formattedPrice = TrendMart.utils.formatMoney(price, TrendMart.moneyFormat);
        const onSale = compare > price;
        let priceHtml = `<div class="tm-price__container">`;
        if (onSale) {
          const formattedCompare = TrendMart.utils.formatMoney(compare, TrendMart.moneyFormat);
          const savePercent = Math.round(((compare - price) * 100) / compare);
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.salePrice || 'Sale price'}</span>`;
          priceHtml += `<span class="tm-price__current tm-price__current--sale">${formattedPrice}</span> `;
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.regularPrice || 'Regular price'}</span>`;
          priceHtml += `<s class="tm-price__compare">${formattedCompare}</s>`;
          if (savePercent > 0) {
            priceHtml += ` <span class="tm-badge tm-badge--sale" aria-hidden="true">-${savePercent}%</span>`;
          }
        } else {
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.regularPrice || 'Regular price'}</span>`;
          priceHtml += `<span class="tm-price__current">${formattedPrice}</span>`;
        }
        priceHtml += `</div>`;
        priceBox.innerHTML = priceHtml;
        if (onSale) {
          priceBox.classList.add('tm-price--on-sale');
        } else {
          priceBox.classList.remove('tm-price--on-sale');
        }

        // Update card sale badge in top-left
        const badgesWrap = card.querySelector('.tm-product-card__badges');
        if (badgesWrap) {
          let saleBadge = badgesWrap.querySelector('.tm-badge--sale');
          if (onSale && Math.round(((compare - price) * 100) / compare) > 0) {
            const savePercent = Math.round(((compare - price) * 100) / compare);
            if (!saleBadge) {
              saleBadge = document.createElement('span');
              saleBadge.className = 'tm-badge tm-badge--sale';
              badgesWrap.prepend(saleBadge);
            }
            saleBadge.textContent = `-${savePercent}%`;
          } else if (saleBadge) {
            saleBadge.remove();
          }
        }
      }
    }

    // 5. Update options button link or quick add button status
    const hasOtherOptions = dot.getAttribute('data-has-other-options') === 'true';
    const isAvailable = dot.getAttribute('data-available') === 'true';

    const optionsBtn = card.querySelector('[data-card-options-btn], .tm-card-select-options-btn');
    if (optionsBtn && variantId) {
      const baseUrl = optionsBtn.getAttribute('data-base-url') || optionsBtn.getAttribute('href')?.split('?')[0];
      if (baseUrl) {
        optionsBtn.setAttribute('href', `${baseUrl}?variant=${variantId}`);
      }
      optionsBtn.setAttribute('data-selected-variant-id', variantId);
    }

    // Update card image and title links to include ?variant=
    const imgLink = card.querySelector('.tm-product-card__img-link');
    if (imgLink && variantId) {
      const base = imgLink.getAttribute('href')?.split('?')[0];
      if (base) imgLink.setAttribute('href', `${base}?variant=${variantId}`);
    }
    const titleLink = card.querySelector('.tm-product-card__title a');
    if (titleLink && variantId) {
      const base = titleLink.getAttribute('href')?.split('?')[0];
      if (base) titleLink.setAttribute('href', `${base}?variant=${variantId}`);
    }

    // If color-only product: toggle button state and text based on variant availability
    const submitBtn = card.querySelector('[data-card-submit-btn]');
    const btnText = card.querySelector('[data-card-btn-text]');
    if (submitBtn && !hasOtherOptions) {
      if (isAvailable) {
        submitBtn.disabled = false;
        submitBtn.classList.remove('is-disabled');
        if (btnText) btnText.textContent = TrendMart.strings?.addToCart || 'Quick Add';
      } else {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-disabled');
        if (btnText) btnText.textContent = TrendMart.strings?.soldOut || 'Sold Out';
      }
    }
  });

  // Helper: Synchronize Card Options (Colors & Sizes)
  function syncCardOptions(card) {
    const variantsRaw = card.getAttribute('data-product-variants');
    if (!variantsRaw) return;

    let variants;
    try {
      variants = JSON.parse(variantsRaw);
    } catch (err) {
      console.error('Error parsing card variants:', err);
      return;
    }

    const hasColor = card.getAttribute('data-has-color') === 'true';
    const hasSize = card.getAttribute('data-has-size') === 'true';
    const colorAttr = card.getAttribute('data-color-index');
    const sizeAttr = card.getAttribute('data-size-index');
    const colorIndex = colorAttr !== null ? parseInt(colorAttr, 10) : 0;
    const sizeIndex = sizeAttr !== null ? parseInt(sizeAttr, 10) : (hasColor ? 1 : 0);

    const activeColorDot = card.querySelector('.tm-mini-color-dot.is-active');
    const chosenColor = activeColorDot ? activeColorDot.getAttribute('data-color-val') : null;

    const activeSizeChip = card.querySelector('.tm-mini-size-chip.is-active');
    const chosenSize = activeSizeChip ? activeSizeChip.getAttribute('data-size-val') : null;

    // 1. Update size chips sold-out status based on chosen color
    if (hasSize) {
      const sizeChips = card.querySelectorAll('.tm-mini-size-chip');
      sizeChips.forEach(chip => {
        const sVal = chip.getAttribute('data-size-val');
        const vMatch = variants.find(v => {
          const cMatch = hasColor && chosenColor ? v.options[colorIndex] === chosenColor : true;
          const sMatch = v.options[sizeIndex] === sVal;
          return cMatch && sMatch;
        });
        if (vMatch && !vMatch.available) {
          chip.classList.add('is-sold-out');
          chip.title = `${sVal} (Sold out)`;
        } else {
          chip.classList.remove('is-sold-out');
          chip.title = sVal;
        }
      });
    }

    // 2. Find matching variant for current selection
    let matchedVariant = null;

    if (hasColor && hasSize) {
      if (chosenColor && chosenSize) {
        matchedVariant = variants.find(v => v.options[colorIndex] === chosenColor && v.options[sizeIndex] === chosenSize);
      } else if (chosenColor) {
        matchedVariant = variants.find(v => v.options[colorIndex] === chosenColor && v.available) ||
                         variants.find(v => v.options[colorIndex] === chosenColor);
      }
    } else if (hasColor && !hasSize) {
      if (chosenColor) {
        matchedVariant = variants.find(v => v.options[colorIndex] === chosenColor && v.available) ||
                         variants.find(v => v.options[colorIndex] === chosenColor);
      }
    } else if (hasSize && !hasColor) {
      if (chosenSize) {
        matchedVariant = variants.find(v => v.options[sizeIndex] === chosenSize);
      }
    }

    if (!matchedVariant) return;

    // 3. Set card form hidden name="id"
    const idInput = card.querySelector('input[name="id"]');
    if (idInput) {
      idInput.value = matchedVariant.id;
      idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // 4. Swap card image
    if (matchedVariant.image) {
      const primaryImg = card.querySelector('.primary-img, .tm-product-card__img-link img');
      if (primaryImg) {
        primaryImg.src = matchedVariant.image;
        if (primaryImg.hasAttribute('srcset')) {
          primaryImg.removeAttribute('srcset');
        }
      }
      const secondaryImg = card.querySelector('.secondary-img');
      if (secondaryImg) {
        secondaryImg.style.display = 'none';
      }
    }

    // 5. Update card price
    if (window.TrendMart && TrendMart.utils && TrendMart.utils.formatMoney) {
      const price = matchedVariant.price;
      const compare = matchedVariant.compare_at_price || 0;
      const priceBox = card.querySelector('.tm-price-box');
      if (priceBox && !isNaN(price)) {
        const formattedPrice = TrendMart.utils.formatMoney(price, TrendMart.moneyFormat);
        const onSale = compare > price;
        let priceHtml = `<div class="tm-price__container">`;
        if (onSale) {
          const formattedCompare = TrendMart.utils.formatMoney(compare, TrendMart.moneyFormat);
          const savePercent = Math.round(((compare - price) * 100) / compare);
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.salePrice || 'Sale price'}</span>`;
          priceHtml += `<span class="tm-price__current tm-price__current--sale">${formattedPrice}</span> `;
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.regularPrice || 'Regular price'}</span>`;
          priceHtml += `<s class="tm-price__compare">${formattedCompare}</s>`;
          if (savePercent > 0) {
            priceHtml += ` <span class="tm-badge tm-badge--sale" aria-hidden="true">-${savePercent}%</span>`;
          }
        } else {
          priceHtml += `<span class="visually-hidden">${TrendMart.strings?.regularPrice || 'Regular price'}</span>`;
          priceHtml += `<span class="tm-price__current">${formattedPrice}</span>`;
        }
        priceHtml += `</div>`;
        priceBox.innerHTML = priceHtml;
        if (onSale) {
          priceBox.classList.add('tm-price--on-sale');
        } else {
          priceBox.classList.remove('tm-price--on-sale');
        }

        // Update card sale badge in top-left
        const badgesWrap = card.querySelector('.tm-product-card__badges');
        if (badgesWrap) {
          let saleBadge = badgesWrap.querySelector('.tm-badge--sale');
          if (onSale && Math.round(((compare - price) * 100) / compare) > 0) {
            const savePercent = Math.round(((compare - price) * 100) / compare);
            if (!saleBadge) {
              saleBadge = document.createElement('span');
              saleBadge.className = 'tm-badge tm-badge--sale';
              badgesWrap.prepend(saleBadge);
            }
            saleBadge.textContent = `-${savePercent}%`;
          } else if (saleBadge) {
            saleBadge.remove();
          }
        }
      }
    }

    // 6. Update product card links (?variant=)
    const imgLink = card.querySelector('.tm-product-card__img-link');
    if (imgLink) {
      const base = imgLink.getAttribute('href')?.split('?')[0];
      if (base) imgLink.setAttribute('href', `${base}?variant=${matchedVariant.id}`);
    }
    const titleLink = card.querySelector('.tm-product-card__title a');
    if (titleLink) {
      const base = titleLink.getAttribute('href')?.split('?')[0];
      if (base) titleLink.setAttribute('href', `${base}?variant=${matchedVariant.id}`);
    }

    // 7. Handle Button: "Select Options" vs "Add to Cart" / "Sold Out"
    const optionsBtn = card.querySelector('[data-card-options-btn]');
    const submitBtn = card.querySelector('[data-card-submit-btn]');
    const btnText = card.querySelector('[data-card-btn-text]');

    const sizeStillNeeded = hasSize && !chosenSize;

    if (sizeStillNeeded) {
      if (optionsBtn) {
        optionsBtn.style.display = '';
        const baseUrl = optionsBtn.getAttribute('data-base-url') || optionsBtn.getAttribute('href')?.split('?')[0];
        if (baseUrl) optionsBtn.setAttribute('href', `${baseUrl}?variant=${matchedVariant.id}`);
        optionsBtn.setAttribute('data-selected-variant-id', matchedVariant.id);
      }
      if (submitBtn) submitBtn.style.display = 'none';
    } else {
      if (optionsBtn) optionsBtn.style.display = 'none';
      if (submitBtn) {
        submitBtn.style.display = '';
        if (matchedVariant.available) {
          submitBtn.disabled = false;
          submitBtn.classList.remove('is-disabled');
          if (btnText) btnText.textContent = TrendMart.strings?.addToCart || 'Add to Cart';
        } else {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-disabled');
          if (btnText) btnText.textContent = TrendMart.strings?.soldOut || 'Sold Out';
        }
      }
    }
  }

  // Delegated Click Listener for Mini Color Dots and Size Chips
  document.addEventListener('click', (e) => {
    const optionBtn = e.target.closest('[data-option-type="color"], [data-option-type="size"]');
    if (!optionBtn) return;

    e.preventDefault();
    e.stopPropagation();

    const card = optionBtn.closest('[data-product-card]');
    if (!card) return;

    const optType = optionBtn.getAttribute('data-option-type');

    if (optType === 'color') {
      const colorsWrap = card.querySelector('[data-mini-colors]');
      if (colorsWrap) {
        colorsWrap.querySelectorAll('.tm-mini-color-dot').forEach(b => b.classList.remove('is-active'));
      }
      optionBtn.classList.add('is-active');
    } else if (optType === 'size') {
      const sizesWrap = card.querySelector('[data-mini-sizes]');
      const wasActive = optionBtn.classList.contains('is-active');
      if (sizesWrap) {
        sizesWrap.querySelectorAll('.tm-mini-size-chip').forEach(b => b.classList.remove('is-active'));
      }
      if (!wasActive) {
        optionBtn.classList.add('is-active');
      }
    }

    syncCardOptions(card);
  });

  // ==========================================================================
  // Product Slider Engine (Columns, Rows & Scroll Navigation + Pagination Numbers)
  // ==========================================================================
  function initProductSliders(context = document) {
    const sliders = context.querySelectorAll('[data-product-slider]');
    sliders.forEach(slider => {
      if (slider._sliderInitialized) {
        if (typeof slider._updateButtons === 'function') {
          slider._updateButtons();
        }
        return;
      }

      const track = slider.querySelector('[data-slider-track]');
      const prevBtn = slider.querySelector('[data-slider-prev]');
      const nextBtn = slider.querySelector('[data-slider-next]');
      const pagination = slider.querySelector('[data-slider-pagination]');
      if (!track) return;

      const updateButtons = () => {
        const maxScroll = track.scrollWidth - track.clientWidth;
        const isOverflowing = maxScroll > 4;

        slider.classList.toggle('is-overflowing', isOverflowing);

        if (prevBtn) {
          const atStart = track.scrollLeft <= 4;
          prevBtn.disabled = atStart;
          prevBtn.classList.toggle('is-disabled', atStart);
        }
        if (nextBtn) {
          const atEnd = track.scrollLeft >= maxScroll - 4;
          nextBtn.disabled = atEnd;
          nextBtn.classList.toggle('is-disabled', atEnd);
        }

        if (pagination) {
          if (!isOverflowing) {
            pagination.style.display = 'none';
          } else {
            pagination.style.display = 'flex';

            // Calculate total pages based on current viewport
            const firstCard = track.querySelector('.tm-product-card');
            const cardWidth = firstCard ? (firstCard.offsetWidth + 14) : 280;
            const visibleCols = Math.max(1, Math.round(track.clientWidth / cardWidth));
            const totalCards = track.querySelectorAll('.tm-product-card').length;
            const rowsAttr = parseInt(slider.getAttribute('data-rows') || '1', 10);
            const pageSize = visibleCols * rowsAttr;
            const totalPages = Math.max(2, Math.ceil(totalCards / (pageSize || 1)));

            let pageBtns = pagination.querySelectorAll('.tm-slider-page-btn');
            if (pageBtns.length !== totalPages) {
              let html = '';
              for (let i = 0; i < totalPages; i++) {
                html += `<button type="button" class="tm-slider-page-btn ${i === 0 ? 'is-active' : ''}" data-page="${i}" aria-label="Go to slide page ${i + 1}">${i + 1}</button>`;
              }
              pagination.innerHTML = html;
              pageBtns = pagination.querySelectorAll('.tm-slider-page-btn');
            }

            // Determine active page
            let activeIndex = 0;
            if (track.scrollLeft >= maxScroll - 12) {
              activeIndex = pageBtns.length - 1;
            } else {
              activeIndex = Math.round(track.scrollLeft / (track.clientWidth || 1));
              activeIndex = Math.max(0, Math.min(pageBtns.length - 1, activeIndex));
            }

            pageBtns.forEach((btn, idx) => {
              btn.classList.toggle('is-active', idx === activeIndex);
            });
          }
        }
      };

      const equalizeCards = () => {
        const rowsAttr = parseInt(slider.getAttribute('data-rows') || '1', 10);
        if (rowsAttr > 1) return;
        const cards = track.querySelectorAll('.tm-product-card');
        if (cards.length <= 1) return;

        cards.forEach(c => { c.style.minHeight = ''; });

        let maxHeight = 0;
        cards.forEach(c => {
          const h = c.offsetHeight;
          if (h > maxHeight) maxHeight = h;
        });

        if (maxHeight > 0) {
          cards.forEach(c => {
            c.style.minHeight = `${maxHeight}px`;
          });
        }
      };

      slider._updateButtons = () => {
        equalizeCards();
        updateButtons();
      };

      if (prevBtn) {
        prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const firstCard = track.querySelector('.tm-product-card');
          const step = firstCard ? (firstCard.offsetWidth + 16) : Math.max(280, track.clientWidth * 0.75);
          track.scrollBy({ left: -step, behavior: 'smooth' });
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const firstCard = track.querySelector('.tm-product-card');
          const step = firstCard ? (firstCard.offsetWidth + 16) : Math.max(280, track.clientWidth * 0.75);
          track.scrollBy({ left: step, behavior: 'smooth' });
        });
      }

      if (pagination) {
        pagination.addEventListener('click', (e) => {
          const btn = e.target.closest('.tm-slider-page-btn');
          if (!btn) return;
          e.preventDefault();
          const pageIndex = parseInt(btn.getAttribute('data-page'), 10);
          if (isNaN(pageIndex)) return;

          const maxScroll = track.scrollWidth - track.clientWidth;
          const pageBtns = pagination.querySelectorAll('.tm-slider-page-btn');
          const totalPages = pageBtns.length;

          let targetScroll = 0;
          if (pageIndex === totalPages - 1) {
            targetScroll = maxScroll;
          } else {
            targetScroll = Math.min(maxScroll, pageIndex * track.clientWidth);
          }

          track.scrollTo({ left: targetScroll, behavior: 'smooth' });
        });
      }

      let scrollTimeout;
      track.addEventListener('scroll', () => {
        clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(updateButtons, 40);
      }, { passive: true });

      if (window.ResizeObserver) {
        const ro = new ResizeObserver(() => {
          equalizeCards();
          updateButtons();
        });
        ro.observe(track);
      } else {
        window.addEventListener('resize', TrendMart.utils.debounce(() => {
          equalizeCards();
          updateButtons();
        }, 150));
      }

      slider._sliderInitialized = true;

      // Initial check & after layout/images settle
      equalizeCards();
      updateButtons();
      setTimeout(() => { equalizeCards(); updateButtons(); }, 250);
      setTimeout(() => { equalizeCards(); updateButtons(); }, 800);
      window.addEventListener('load', () => { equalizeCards(); updateButtons(); });
    });
  }

  TrendMart.initProductSliders = initProductSliders;
  initProductSliders();

  // High-End Viewport Scroll Reveal Engine (Agency-Tier)
  function initScrollReveals() {
    const revealElements = document.querySelectorAll('[data-scroll-reveal], .tm-section-header, .tm-visual-category-grid, .tm-product-grid--editorial, .tm-featured-split, .tm-brand-story, .tm-reviews-grid, .tm-newsletter-box');
    
    if (!('IntersectionObserver' in window)) {
      revealElements.forEach(el => el.classList.add('is-revealed'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '0px 0px -40px 0px',
      threshold: 0.08
    });

    revealElements.forEach(el => {
      if (!el.hasAttribute('data-scroll-reveal')) {
        el.setAttribute('data-scroll-reveal', '');
      }
      observer.observe(el);
    });
  }

  initScrollReveals();
  TrendMart.initScrollReveals = initScrollReveals;
});
